import { apiService } from './apiService';
import { API_ENDPOINTS } from '../constants/apiConstants';

export const userService = {
  // Get all users
  getUsers: async (params = {}) => {
    try {
      const config = Object.keys(params).length ? { params } : {};
      const response = await apiService.get(API_ENDPOINTS.USER, config);

      return {
        success: true,
        data: response.data.result || response.data,
        message: response.data?.message || 'Users fetched successfully'
      };
    } catch (error) {
      console.error('Get users error:', error);

      let errorMessage = 'Failed to fetch users. Please try again.';

      if (error.response) {
        // Check for 'detail' field (FastAPI style) or 'message' field
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  },

  // Get user by ID
  getUserById: async (userId) => {
    try {
      const response = await apiService.get(`${API_ENDPOINTS.USER}${userId}`);

      return {
        success: true,
        data: response.data.result || response.data,
        message: response.data?.message || 'User fetched successfully'
      };
    } catch (error) {
      console.error('Get user by ID error:', error);

      let errorMessage = 'Failed to fetch user. Please try again.';

      if (error.response) {
        // Check for 'detail' field (FastAPI style) or 'message' field
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  },

  // Get detailed user info (charts, conversations, reports)
  getUserDetails: async (userId, profileId = "") => {
    try {
      const config = profileId ? { params: { profile_id: profileId } } : {};
      const response = await apiService.get(`${API_ENDPOINTS.USER}user-details/${userId}`, config);

      return {
        success: true,
        data: response.data.result || response.data,
        message: response.data?.message || 'User details fetched successfully'
      };
    } catch (error) {
      console.error('Get user details error:', error);

      let errorMessage = 'Failed to fetch user details. Please try again.';
      const status = error.response?.status || null;

      if (error.response) {
        const apiError = error.response.data?.detail || error.response.data?.message || error.response.data;
        if (status === 404) {
          errorMessage = apiError || 'User not found.';
        } else if (status === 204) {
          errorMessage = apiError || 'No content found for the user.';
        } else {
          errorMessage = apiError || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else {
        errorMessage = error.message || errorMessage;
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message,
        status
      };
    }
  },

  // Delete a user
  deleteUser: async (userId) => {
    try {
      const response = await apiService.delete(`${API_ENDPOINTS.USER}${userId}`);

      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'User deleted successfully'
      };
    } catch (error) {
      console.error('Delete user error:', error);

      let errorMessage = 'Failed to delete user. Please try again.';

      if (error.response) {
        // Check for 'detail' field (FastAPI style) or 'message' field
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  },

  // Block/Unblock a user
  blockUser: async (userId, isBlocked) => {
    try {
      const response = await apiService.patch(`${API_ENDPOINTS.USER}block/${userId}`);

      return {
        success: true,
        data: response.data,
        message: response.data?.message || `User ${isBlocked ? 'blocked' : 'unblocked'} successfully`
      };
    } catch (error) {
      console.error('Block user error:', error);

      let errorMessage = `Failed to ${isBlocked ? 'block' : 'unblock'} user. Please try again.`;

      if (error.response) {
        // Check for 'detail' field (FastAPI style) or 'message' field
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  },

  // Unblock a user
  unblockUser: async (userId) => {
    try {
      const response = await apiService.patch(`${API_ENDPOINTS.USER}unblock/${userId}`);

      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'User unblocked successfully'
      };
    } catch (error) {
      console.error('Unblock user error:', error);

      let errorMessage = 'Failed to unblock user. Please try again.';

      if (error.response) {
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  },

  // Get chart image for a specific user and chart type
  getChartImage: async (userId, chartId, profileId = "") => {
    try {
      const params = new URLSearchParams({ chart: chartId });
      if (profileId) params.set("profile_id", profileId);
      const response = await apiService.post(
        `${API_ENDPOINTS.USER}user-details/chart-image/${userId}?${params.toString()}`
      );

      return {
        success: true,
        data: response.data.result || response.data,
        message: response.data?.message || 'Chart image fetched successfully'
      };
    } catch (error) {
      console.error('Get chart image error:', error);

      let errorMessage = 'Failed to fetch chart image. Please try again.';

      if (error.response) {
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  }

  // Get chat history by user ID and chat ID
  , getChatHistory: async (userId, chatId) => {
    try {
      const response = await apiService.get(`/astrology/admin/chat-history/${chatId}/${userId}`);

      return {
        success: true,
        data: response.data.result || response.data,
        message: response.data?.message || 'Chat history fetched successfully'
      };
    } catch (error) {
      console.error('Get chat history error:', error);

      let errorMessage = 'Failed to fetch chat history. Please try again.';

      if (error.response) {
        const apiError = error.response.data?.detail || error.response.data?.message;
        errorMessage = apiError || errorMessage;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message
      };
    }
  },

  // Get user stats
  getUserStats: async (userId) => {
    try {
      const response = await apiService.get(`${API_ENDPOINTS.USER}stats/${userId}`);
      return {
        success: true,
        data: response.data.result || response.data,
        message: response.data?.message || 'User stats fetched successfully'
      };
    } catch (error) {
      console.error('Get user stats error:', error);
      return {
        success: false,
        message: 'Failed to fetch user stats.',
        error: error.response?.data || error.message
      };
    }
  }
};
